import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const ScheduleTab = ({ matches, matchPredictions = {}, tournament, onNavigateToMatch }) => {
  const [toastMessage, setToastMessage] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({
    completed: true,
    thisWeek: false,
    future: true,
  });

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev?.[section] }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleTimeString('en-US', { 
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getMatchStatus = (match) => {
    const now = new Date();
    const matchDate = new Date(match?.dateTime);
    
    if (match?.status === 'completed') return { text: 'Completed', color: 'success' };
    if (match?.status === 'cancelled') return { text: 'Cancelled', color: 'warning' };
    if (match?.status === 'live') return { text: 'Live', color: 'error' };
    if (matchDate < now) return { text: 'In Progress', color: 'warning' };
    return { text: 'Upcoming', color: 'muted' };
  };

  const groupMatchesBySection = () => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfThisWeek = new Date(startOfToday);
    endOfThisWeek?.setDate(endOfThisWeek?.getDate() + 7);

    const completed = [];
    const thisWeek = [];
    const future = [];

    matches?.forEach(match => {
      const matchDate = new Date(match?.dateTime);
      if (match?.status === 'completed' || match?.status === 'cancelled') {
        completed?.push(match);
      } else if (matchDate >= startOfToday && matchDate < endOfThisWeek) {
        thisWeek?.push(match);
      } else {
        future?.push(match);
      }
    });

    // Sort completed descending (most recent first), others ascending
    completed?.sort((a, b) => new Date(b?.dateTime) - new Date(a?.dateTime));
    thisWeek?.sort((a, b) => new Date(a?.dateTime) - new Date(b?.dateTime));
    future?.sort((a, b) => new Date(a?.dateTime) - new Date(b?.dateTime));

    return { completed, thisWeek, future };
  };

  const renderMatch = (match) => {
    const status = getMatchStatus(match);
    const prediction = matchPredictions?.[match?.id] || null;
    const hasPrediction = !!prediction;
    const isCompleted = match?.status === 'completed';

    const getTeamName = (teamId) => {
      if (!teamId) return null;
      if (match?.team1?.id === teamId) return match?.team1?.name;
      if (match?.team2?.id === teamId) return match?.team2?.name;
      return null;
    };

    const getPlayerName = (playerId) => {
      if (!playerId) return null;
      return tournament?.players?.find(p => p?.id === playerId)?.name || null;
    };

    const isCorrect = (predictedId, actualId) => {
      if (!predictedId || !actualId) return null;
      return predictedId === actualId;
    };

    const matchSettings = tournament?.predictionSettings?.matchLevel || {};

    const getPoints = (settingKey) => {
      return matchSettings?.[settingKey]?.points || 0;
    };

    // Build prediction rows — for completed matches without prediction, show "Missed" as pick
    const predictionRows = [];

    const isGroupStage = match?.stage === 'group';
    // Determine which categories are enabled for this match
    const enabledCategories = [];
    if (matchSettings?.matchWinner?.enabled !== false) enabledCategories?.push('matchWinner');
    if (matchSettings?.tossWinner?.enabled !== false && !isGroupStage) enabledCategories?.push('tossWinner');
    if (matchSettings?.battingFirst?.enabled !== false && !isGroupStage) enabledCategories?.push('battingFirst');
    if (matchSettings?.manOfMatch?.enabled !== false) enabledCategories?.push('manOfMatch');
    if (matchSettings?.highestRunScorer?.enabled !== false && !isGroupStage) enabledCategories?.push('highestRunScorer');
    if (matchSettings?.highestWicketTaker?.enabled !== false && !isGroupStage) enabledCategories?.push('highestWicketTaker');

    const categoryConfig = {
      matchWinner: { label: 'Match Winner', predKey: 'winnerId', resultKey: 'winnerId', getName: getTeamName, pointsKey: 'matchWinner', defaultPts: 10 },
      tossWinner: { label: 'Toss Winner', predKey: 'tossWinnerId', resultKey: 'tossWinnerId', getName: getTeamName, pointsKey: 'tossWinner', defaultPts: 5 },
      battingFirst: { label: 'Batting First', predKey: 'battingFirstId', resultKey: 'battingFirstId', getName: getTeamName, pointsKey: 'battingFirst', defaultPts: 5 },
      manOfMatch: { label: 'Man of the Match', predKey: 'manOfMatchId', resultKey: 'manOfMatchId', getName: getPlayerName, pointsKey: 'manOfMatch', defaultPts: 15 },
      highestRunScorer: { label: 'Highest Scorer', predKey: 'highestScorerId', resultKey: 'highestScorerId', getName: getPlayerName, pointsKey: 'highestRunScorer', defaultPts: 20 },
      highestWicketTaker: { label: 'Highest Wicket Taker', predKey: 'highestWicketTakerId', resultKey: 'highestWicketTakerId', getName: getPlayerName, pointsKey: 'highestWicketTaker', defaultPts: 20 },
    };

    if (isCompleted && !hasPrediction) {
      // Show all enabled categories with "Missed" as pick
      enabledCategories?.forEach(catKey => {
        const cfg = categoryConfig?.[catKey];
        const actualId = match?.[cfg?.resultKey];
        const pts = getPoints(cfg?.pointsKey) || cfg?.defaultPts;
        predictionRows?.push({
          label: cfg?.label,
          yourPick: 'Missed',
          actual: actualId ? (cfg?.getName(actualId) || '—') : null,
          correct: false,
          pts: 0,
          hasResult: !!actualId,
          missed: true,
        });
      });
    } else {
      // Original logic for users who predicted
      if (prediction?.winnerId) {
        const correct = match?.winnerId ? isCorrect(prediction?.winnerId, match?.winnerId) : null;
        const pts = correct ? (getPoints('matchWinner') || 10) : 0;
        predictionRows?.push({
          label: 'Match Winner',
          yourPick: getTeamName(prediction?.winnerId) || '—',
          actual: match?.winnerId ? (getTeamName(match?.winnerId) || '—') : null,
          correct,
          pts,
          hasResult: !!match?.winnerId,
        });
      }

      if (prediction?.tossWinnerId) {
        const correct = match?.tossWinnerId ? isCorrect(prediction?.tossWinnerId, match?.tossWinnerId) : null;
        const pts = correct ? (getPoints('tossWinner') || 5) : 0;
        predictionRows?.push({
          label: 'Toss Winner',
          yourPick: getTeamName(prediction?.tossWinnerId) || '—',
          actual: match?.tossWinnerId ? (getTeamName(match?.tossWinnerId) || '—') : null,
          correct,
          pts,
          hasResult: !!match?.tossWinnerId,
        });
      }

      if (prediction?.battingFirstId) {
        const correct = match?.battingFirstId ? isCorrect(prediction?.battingFirstId, match?.battingFirstId) : null;
        const pts = correct ? (getPoints('battingFirst') || 5) : 0;
        predictionRows?.push({
          label: 'Batting First',
          yourPick: getTeamName(prediction?.battingFirstId) || '—',
          actual: match?.battingFirstId ? (getTeamName(match?.battingFirstId) || '—') : null,
          correct,
          pts,
          hasResult: !!match?.battingFirstId,
        });
      }

      if (prediction?.manOfMatchId) {
        const correct = match?.manOfMatchId ? isCorrect(prediction?.manOfMatchId, match?.manOfMatchId) : null;
        const pts = correct ? (getPoints('manOfMatch') || 15) : 0;
        predictionRows?.push({
          label: 'Man of the Match',
          yourPick: getPlayerName(prediction?.manOfMatchId) || '—',
          actual: match?.manOfMatchId ? (getPlayerName(match?.manOfMatchId) || '—') : null,
          correct,
          pts,
          hasResult: !!match?.manOfMatchId,
        });
      }

      if (prediction?.highestScorerId) {
        const correct = match?.highestScorerId ? isCorrect(prediction?.highestScorerId, match?.highestScorerId) : null;
        const pts = correct ? (getPoints('highestRunScorer') || 20) : 0;
        predictionRows?.push({
          label: 'Highest Scorer',
          yourPick: getPlayerName(prediction?.highestScorerId) || '—',
          actual: match?.highestScorerId ? (getPlayerName(match?.highestScorerId) || '—') : null,
          correct,
          pts,
          hasResult: !!match?.highestScorerId,
        });
      }

      if (prediction?.highestWicketTakerId) {
        const correct = match?.highestWicketTakerId ? isCorrect(prediction?.highestWicketTakerId, match?.highestWicketTakerId) : null;
        const pts = correct ? (getPoints('highestWicketTaker') || 20) : 0;
        predictionRows?.push({
          label: 'Highest Wicket Taker',
          yourPick: getPlayerName(prediction?.highestWicketTakerId) || '—',
          actual: match?.highestWicketTakerId ? (getPlayerName(match?.highestWicketTakerId) || '—') : null,
          correct,
          pts,
          hasResult: !!match?.highestWicketTakerId,
        });
      }
    }

    const totalPtsEarned = predictionRows?.filter(r => r?.hasResult)?.reduce((sum, r) => sum + (r?.correct ? r?.pts : 0), 0);
    const correctCount = predictionRows?.filter(r => r?.hasResult && r?.correct === true)?.length;
    const wrongCount = predictionRows?.filter(r => r?.hasResult && r?.correct === false && !r?.missed)?.length;
    const resolvedCount = predictionRows?.filter(r => r?.hasResult)?.length;

    const maxPossible = predictionRows?.reduce((sum, r) => {
      const maxPts = r?.label === 'Match Winner' ? (getPoints('matchWinner') || 10)
        : r?.label === 'Toss Winner' ? (getPoints('tossWinner') || 5)
        : r?.label === 'Batting First' ? (getPoints('battingFirst') || 5)
        : r?.label === 'Man of the Match' ? (getPoints('manOfMatch') || 15)
        : r?.label === 'Highest Scorer' ? (getPoints('highestRunScorer') || 20)
        : (getPoints('highestWicketTaker') || 20);
      return sum + maxPts;
    }, 0);

    // Show prediction panel for: users who predicted, OR completed matches (missed)
    const showPredictionPanel = hasPrediction || isCompleted;

    return (
      <div key={match?.id} className="p-4 md:p-6 hover:bg-muted/30 transition-colors">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <span className={`px-2 py-1 text-xs font-semibold rounded-full bg-${status?.color}/10 text-${status?.color}`}>
                {status?.text}
              </span>
              {hasPrediction && (
                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-success/10 text-success flex items-center gap-1">
                  <Icon name="CheckCircle" size={14} />
                  Predicted
                </span>
              )}
              {isCompleted && !hasPrediction && (
                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-muted text-muted-foreground flex items-center gap-1">
                  <Icon name="AlertCircle" size={14} />
                  Not Predicted
                </span>
              )}
              <span className="text-xs text-muted-foreground">{match?.matchType}</span>
              <span className="text-xs text-muted-foreground">•</span>
              <span className="text-xs text-muted-foreground">{match?.stage}</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-4 items-center">
              <div className="flex items-center gap-3">
                <Image
                  src={match?.team1?.logo}
                  alt={match?.team1?.logoAlt}
                  className="w-10 h-10 md:w-12 md:h-12 object-contain"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground text-sm md:text-base truncate">
                    {match?.team1?.name}
                  </p>
                  {match?.team1?.score && match?.status === 'completed' && (
                    <p className={`text-xs md:text-sm font-semibold ${match?.winnerId && match?.team1?.id === match?.winnerId ? 'text-success' : match?.winnerId ? 'text-error' : 'text-muted-foreground'}`}>
                      {match?.team1?.score}{match?.team1?.wickets !== undefined && match?.team1?.wickets !== '' && match?.team1?.wickets !== null ? `/${match?.team1?.wickets}` : ''}
                    </p>
                  )}
                  {match?.team1?.score && match?.status !== 'completed' && (
                    <p className="text-xs md:text-sm text-muted-foreground">
                      {match?.team1?.score}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-center">
                <span className="text-lg md:text-xl font-bold text-muted-foreground">vs</span>
              </div>

              <div className="flex items-center gap-3">
                <Image
                  src={match?.team2?.logo}
                  alt={match?.team2?.logoAlt}
                  className="w-10 h-10 md:w-12 md:h-12 object-contain"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground text-sm md:text-base truncate">
                    {match?.team2?.name}
                  </p>
                  {match?.team2?.score && match?.status === 'completed' && (
                    <p className={`text-xs md:text-sm font-semibold ${match?.winnerId && match?.team2?.id === match?.winnerId ? 'text-success' : match?.winnerId ? 'text-error' : 'text-muted-foreground'}`}>
                      {match?.team2?.score}{match?.team2?.wickets !== undefined && match?.team2?.wickets !== '' && match?.team2?.wickets !== null ? `/${match?.team2?.wickets}` : ''}
                    </p>
                  )}
                  {match?.team2?.score && match?.status !== 'completed' && (
                    <p className="text-xs md:text-sm text-muted-foreground">
                      {match?.team2?.score}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 mt-3 text-xs md:text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Icon name="Clock" size={16} />
                <span>{formatDate(match?.dateTime)} • {formatTime(match?.dateTime)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="MapPin" size={16} />
                <span>{match?.venue}</span>
              </div>
            </div>

            {/* Prediction Panel */}
            {showPredictionPanel && predictionRows?.length > 0 && (
              <div className="mt-4 border border-border rounded-lg overflow-hidden">
                {/* Panel Header */}
                <div className={`px-3 py-2 flex items-center justify-between ${isCompleted ? 'bg-primary/5 border-b border-border' : 'bg-muted/40'}`}>
                  <div className="flex items-center gap-2">
                    <Icon name="Target" size={14} color="var(--color-primary)" />
                    <span className="text-xs font-semibold text-foreground">My Predictions</span>
                    {isCompleted && !hasPrediction && (
                      <span className="text-xs text-muted-foreground italic">(not submitted)</span>
                    )}
                  </div>
                  {isCompleted && resolvedCount > 0 && hasPrediction && (
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1 text-xs font-semibold text-success">
                        <Icon name="CheckCircle" size={12} />
                        {correctCount} correct
                      </span>
                      <span className="flex items-center gap-1 text-xs font-semibold text-error">
                        <Icon name="XCircle" size={12} />
                        {wrongCount} wrong
                      </span>
                    </div>
                  )}
                </div>

                {/* Completed match: side-by-side comparison table */}
                {isCompleted ? (
                  <>
                    {/* Column headers */}
                    <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 px-3 py-1.5 bg-muted/20 border-b border-border">
                      <span className="text-xs font-semibold text-muted-foreground">Category</span>
                      <span className="text-xs font-semibold text-muted-foreground">Your Pick</span>
                      <span className="text-xs font-semibold text-muted-foreground">Actual Result</span>
                      <span className="text-xs font-semibold text-muted-foreground text-right">Pts</span>
                    </div>
                    <div className="divide-y divide-border">
                      {predictionRows?.map((row, idx) => (
                        <div
                          key={idx}
                          className={`grid grid-cols-[1fr_1fr_1fr_auto] gap-2 px-3 py-2.5 items-center ${
                            row?.missed
                              ? 'bg-muted/10'
                              : row?.hasResult
                                ? row?.correct
                                  ? 'bg-success/5' :'bg-error/5' :''
                          }`}
                        >
                          {/* Category */}
                          <span className="text-xs text-muted-foreground font-medium">{row?.label}</span>

                          {/* Your Pick */}
                          <div className="flex items-center gap-1.5 min-w-0">
                            {row?.missed ? (
                              <span className="text-xs font-semibold text-muted-foreground italic">Missed</span>
                            ) : (
                              <>
                                {row?.hasResult && (
                                  row?.correct
                                    ? <Icon name="CheckCircle" size={13} className="text-success shrink-0" />
                                    : <Icon name="XCircle" size={13} className="text-error shrink-0" />
                                )}
                                <span className={`text-xs font-semibold truncate ${
                                  row?.hasResult
                                    ? row?.correct
                                      ? 'text-success' :'text-error' :'text-foreground'
                                }`}>
                                  {row?.yourPick}
                                </span>
                              </>
                            )}
                          </div>

                          {/* Actual Result */}
                          <div className="min-w-0">
                            {row?.hasResult ? (
                              <span className="text-xs font-semibold text-foreground truncate block">{row?.actual}</span>
                            ) : (
                              <span className="text-xs text-muted-foreground italic">Pending</span>
                            )}
                          </div>

                          {/* Points */}
                          <div className="text-right shrink-0">
                            {row?.missed ? (
                              <span className="text-xs text-muted-foreground">—</span>
                            ) : row?.hasResult ? (
                              <span className={`text-xs font-bold ${row?.correct ? 'text-success' : 'text-muted-foreground'}`}>
                                {row?.correct ? `+${row?.pts}` : '0'}
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Summary footer — only for users who predicted */}
                    {resolvedCount > 0 && hasPrediction && (
                      <div className="px-3 py-2.5 bg-muted/30 border-t border-border flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Icon name="CheckCircle" size={12} className="text-success" />
                            <span className="font-semibold text-success">{correctCount}</span> correct
                          </span>
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Icon name="XCircle" size={12} className="text-error" />
                            <span className="font-semibold text-error">{wrongCount}</span> wrong
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Icon name="Star" size={13} className="text-warning" />
                          <span className="text-xs font-bold text-foreground">
                            {totalPtsEarned} / {maxPossible} pts
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  /* Upcoming match: simple prediction list */
                  (<div className="divide-y divide-border">
                    {predictionRows?.map((row, idx) => (
                      <div key={idx} className="flex items-center justify-between px-3 py-2.5 gap-2">
                        <span className="text-xs text-muted-foreground w-32 shrink-0">{row?.label}</span>
                        <span className="text-xs font-semibold text-foreground flex-1 min-w-0 truncate">{row?.yourPick}</span>
                        <span className="text-xs text-muted-foreground shrink-0 italic">Pending</span>
                      </div>
                    ))}
                  </div>)
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row lg:flex-col gap-2">
            {match?.predictionsEnabled ? (
              <Button
                variant="outline"
                size="sm"
                iconName="Target"
                iconPosition="left"
                onClick={() => onNavigateToMatch(match?.id)}
                className="w-full sm:w-auto"
              >
                {isCompleted ? 'View' : hasPrediction ? 'Edit Predictions' : 'Predict'}
              </Button>
            ) : match?.status !== 'completed' && (
              <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-muted text-muted-foreground border border-border w-full sm:w-auto justify-center">
                <Icon name="Clock" size={14} />
                Predictions Opening Soon
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSection = (key, label, icon, matchList, accentClass) => {
    const isCollapsed = collapsedSections?.[key];
    return (
      <div className="bg-card rounded-lg shadow-elevation-sm overflow-hidden">
        <button
          onClick={() => toggleSection(key)}
          className={`w-full ${accentClass} px-4 md:px-6 py-3 border-b border-border flex items-center justify-between hover:opacity-90 transition-opacity`}
        >
          <h3 className="text-base md:text-lg font-bold text-foreground flex items-center gap-2">
            <Icon name={icon} size={20} color="var(--color-primary)" />
            {label}
            <span className="text-sm font-normal text-muted-foreground">({matchList?.length})</span>
          </h3>
          <Icon name={isCollapsed ? 'ChevronDown' : 'ChevronUp'} size={20} className="text-muted-foreground" />
        </button>
        {!isCollapsed && (
          <div className="divide-y divide-border">
            {matchList?.length > 0 ? (
              matchList?.map(match => renderMatch(match))
            ) : (
              <div className="p-6 text-center text-muted-foreground text-sm">No matches in this section</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const { completed, thisWeek, future } = groupMatchesBySection();

  return (
    <>
      {toastMessage && (
        <div className="fixed bottom-4 right-4 z-[9999] bg-destructive text-destructive-foreground px-4 py-3 rounded-lg shadow-lg text-sm font-medium animate-in fade-in slide-in-from-bottom-4">
          {toastMessage}
        </div>
      )}
      <div className="space-y-4">
        {renderSection('completed', 'Completed', 'CheckCircle', completed, 'bg-muted/40')}
        {renderSection('thisWeek', 'This Week', 'CalendarDays', thisWeek, 'bg-primary/10')}
        {renderSection('future', 'Future', 'Calendar', future, 'bg-muted/40')}
      </div>
    </>
  );
};

export default ScheduleTab;